import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

import { api, durableBusinessPost } from '../../lib/api';
import { compterCaisse, fixerSoldeOuverture, livreDeCaisse } from '../../lib/argentApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatMontant, formatNombre } from '../../lib/formats';
import { libelle, nomLisible, texteSur } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { financialKey } from '../FinancialStatement';
import { Aide } from '../ui/aide';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';
import DetailEcriture from './DetailEcriture';

/**
 * Onglet « Caisse et banque » de l'écran Argent (revue de l'écran Argent, lot 4).
 *
 * À gauche les comptes et leur solde; à droite le livre de caisse du compte
 * choisi, comme un classeur: du plus récent au plus ancien, Entrée et Sortie
 * dans deux colonnes, le solde après chaque ligne, les totaux de la période en
 * pied. Le tri, le solde courant et la pagination viennent du serveur
 * (`GET /finance/accounts/{id}/ledger`), pas du navigateur. Un clic sur une
 * ligne ouvre son détail; « Compter la caisse » enregistre un comptage et peut
 * passer l'écart au journal; un solde d'ouverture inconnu se complète ici.
 */

export const SENS_LIVRE = [
  { cle: '', titre: 'Tout' },
  { cle: 'entrees', titre: 'Entrées' },
  { cle: 'sorties', titre: 'Sorties' },
  { cle: 'transferts', titre: 'Transferts' },
  { cle: 'controles', titre: 'Comptages' },
];

const aujourdhui = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Le libellé d'une ligne du livre: le qui d'abord, la nature ensuite. */
export const libelleLigne = (ligne) => {
  if (ligne.kind === 'BALANCE_CHECK') return { qui: 'Comptage de la caisse', nature: null };
  const nature = libelle('financeKind', ligne.kind);
  if (ligne.kind === 'TRANSFER' && ligne.counterpart) {
    return { qui: `${ligne.counterpart.direction === 'depuis' ? 'Depuis' : 'Vers'} ${nomLisible(ligne.counterpart.name, 'un autre compte')}`, nature };
  }
  return { qui: ligne.party_name ? nomLisible(ligne.party_name) : nature, nature: ligne.party_name ? nature : null };
};

const ETATS = {
  attente: 'En attente',
  annulee: 'Annulée',
  avant: 'Avant l’ouverture',
  autre: 'Sans effet',
  controle: 'Comptage',
};

const montantOuVide = (valeur, devise) => (valeur == null || Number(valeur) === 0 ? '' : formatMontant(valeur, devise));

/** Écart d'un comptage: compté moins journal (positif = excédent, négatif = manque). */
export const ecartComptage = (compte, journal) => {
  if (compte === '' || compte == null || journal == null) return null;
  const n = Number(compte) - Number(journal);
  return Number.isFinite(n) ? Math.round(n) : null;
};

function CompterCaisse({ compte, ouvert, onFermer, onTermine }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [passer, setPasser] = useState(false);
  const [cle, setCle] = useState(financialKey);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    if (!ouvert) return;
    setMontant('');
    setMotif('');
    setPasser(false);
    setErreur('');
    setCle(financialKey());
  }, [ouvert]);
  const devise = compte?.currency || 'XOF';
  const journal = compte?.balance;
  const ecart = ecartComptage(montant, journal);
  const modifier = (setter) => (valeur) => {
    setter(valeur);
    setCle(financialKey());
  };
  const enregistrer = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      const controle = await compterCaisse(compte.id, { montant, motif: motif.trim(), cle });
      if (passer && ecart) {
        // L'écart passe au journal par les écritures ordinaires: une dépense
        // (manque) ou une entrée (excédent) sur ce compte, avec le motif.
        await durableBusinessPost('finance_operation', 'create', '/finance/operations', {
          idempotency_key: `${cle}-ecart`,
          kind: ecart < 0 ? 'EXPENSE' : 'INCOME',
          amount: Math.abs(ecart),
          currency: devise,
          account_id: compte.id,
          occurred_at: aujourdhui(),
          ...(ecart < 0 ? { category: 'AUTRES' } : {}),
          details: { reason: `Écart de caisse · ${motif.trim()}`, notes: 'Écart de comptage de la caisse', source: 'cash_count', check_id: controle?.id },
        });
      }
      toast.success(passer && ecart ? 'Comptage enregistré et écart passé au journal.' : 'Comptage enregistré.');
      onTermine?.();
    } catch (e) {
      setErreur(messageErreur(e, 'Le comptage n’a pas pu être enregistré. Réessayez.'));
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="compter-caisse">
        <DialogHeader>
          <DialogTitle>Compter la caisse · {nomLisible(compte?.name, 'Compte')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {erreur && <p role="alert" className="text-sm text-destructive">{erreur}</p>}
          <div className="flex items-baseline justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Solde du journal</span>
            <strong className="tabular-nums">{journal == null ? 'À compléter' : formatMontant(journal, devise)}</strong>
          </div>
          <label className="block text-sm font-medium">
            Montant compté (FCFA)
            <Input aria-label="Montant compté" type="number" inputMode="numeric" value={montant} onChange={(e) => modifier(setMontant)(e.target.value)} className="mt-1" autoFocus />
          </label>
          {ecart !== null && (
            <div data-testid="ecart-comptage" className={cn('flex items-baseline justify-between rounded-lg border px-3 py-2 text-sm', ecart === 0 ? 'border-success/30 text-success' : 'border-flame/30 bg-flame/10 text-flame-ink')}>
              <span>{ecart === 0 ? 'Aucun écart' : ecart < 0 ? 'Manque' : 'Excédent'}</span>
              {ecart !== 0 && <strong className="tabular-nums">{ecart < 0 ? '− ' : '+ '}{formatMontant(Math.abs(ecart), devise)}</strong>}
            </div>
          )}
          <label className="block text-sm font-medium">
            Observation
            <Input aria-label="Observation" value={motif} onChange={(e) => modifier(setMotif)(e.target.value)} className="mt-1" placeholder="Comptage du soir, qui a compté…" />
          </label>
          {Boolean(ecart) && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={passer} onChange={(e) => modifier(setPasser)(e.target.checked)} className="h-4 w-4" />
              {ecart < 0 ? 'Passer le manque en dépense' : 'Passer l’excédent en entrée'}
              <Aide titre="Passer l’écart au journal">
                Le comptage garde toujours le solde du journal et l’écart. Cochée, la case ajoute en plus une écriture sur ce compte (dépense « Autres » pour un manque, entrée pour un excédent) : le solde du journal rejoint alors le montant compté.
              </Aide>
            </label>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onFermer}>Annuler</Button>
          <Button type="button" disabled={envoi || !enLigne || montant === '' || !motif.trim()} onClick={enregistrer} title={!enLigne ? 'Réseau nécessaire' : undefined}>
            {envoi ? 'Enregistrement…' : 'Enregistrer le comptage'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SoldeOuverture({ compte, ouvert, onFermer, onTermine }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState('');
  const [motif, setMotif] = useState('');
  const [cle, setCle] = useState(financialKey);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    if (!ouvert || !compte) return;
    setMontant(compte.opening_balance == null ? '' : String(Math.round(Number(compte.opening_balance))));
    // Par défaut, la date du premier mouvement: tous les mouvements comptent.
    setDate(String(compte.opening_date || compte.first_movement_date || aujourdhui()).slice(0, 10));
    setMotif('');
    setErreur('');
    setCle(financialKey());
  }, [ouvert, compte]);
  const modifier = (setter) => (valeur) => {
    setter(valeur);
    setCle(financialKey());
  };
  const enregistrer = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      await fixerSoldeOuverture(compte.id, { montant, date, motif: motif.trim(), cle });
      toast.success('Solde d’ouverture enregistré.');
      onTermine?.();
    } catch (e) {
      setErreur(messageErreur(e, 'Le solde d’ouverture n’a pas pu être enregistré.'));
    } finally {
      setEnvoi(false);
    }
  };
  const corrige = compte?.opening_balance != null;
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="solde-ouverture">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            {corrige ? 'Corriger le solde d’ouverture' : 'Compléter le solde d’ouverture'}
            <Aide titre="Solde d’ouverture">
              L’argent présent sur le compte à cette date. Les mouvements à partir de cette date s’y ajoutent ; ceux d’avant restent visibles sans effet. L’ancien et le nouveau solde sont gardés au journal d’audit avec le motif.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {erreur && <p role="alert" className="text-sm text-destructive">{erreur}</p>}
          <p className="text-sm text-muted-foreground">{nomLisible(compte?.name, 'Compte')} · {libelle('compteTresorerie', compte?.account_type)}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium">
              Solde (FCFA)
              <Input aria-label="Solde d’ouverture" type="number" inputMode="numeric" value={montant} onChange={(e) => modifier(setMontant)(e.target.value)} className="mt-1" autoFocus />
            </label>
            <label className="block text-sm font-medium">
              Au
              <Input aria-label="Date du solde" type="date" value={date} onChange={(e) => modifier(setDate)(e.target.value)} className="mt-1" />
            </label>
          </div>
          <label className="block text-sm font-medium">
            Motif
            <Input aria-label="Motif" value={motif} onChange={(e) => modifier(setMotif)(e.target.value)} className="mt-1" placeholder="Relevé bancaire, comptage d’ouverture…" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onFermer}>Annuler</Button>
          <Button type="button" disabled={envoi || !enLigne || montant === '' || !date || !motif.trim()} onClick={enregistrer}>
            {envoi ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NouveauCompte({ ouvert, onFermer, onTermine }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [nom, setNom] = useState('');
  const [type, setType] = useState('CASH');
  const [solde, setSolde] = useState('');
  const [date, setDate] = useState(aujourdhui());
  const [cle, setCle] = useState(financialKey);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    if (!ouvert) return;
    setNom('');
    setType('CASH');
    setSolde('');
    setDate(aujourdhui());
    setErreur('');
    setCle(financialKey());
  }, [ouvert]);
  const modifier = (setter) => (valeur) => {
    setter(valeur);
    setCle(financialKey());
  };
  const enregistrer = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      await api.post('/finance/accounts', {
        name: nom.trim(),
        account_type: type,
        currency: 'XOF',
        opening_balance: solde === '' ? null : Number(solde),
        opening_date: solde === '' ? null : date,
        idempotency_key: cle,
      });
      toast.success('Compte créé.');
      onTermine?.();
    } catch (e) {
      setErreur(messageErreur(e, 'Le compte n’a pas pu être créé.'));
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="nouveau-compte">
        <DialogHeader>
          <DialogTitle>Ajouter un compte</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {erreur && <p role="alert" className="text-sm text-destructive">{erreur}</p>}
          <label className="block text-sm font-medium">
            Nom
            <Input aria-label="Nom du compte" value={nom} onChange={(e) => modifier(setNom)(e.target.value)} className="mt-1" placeholder="Caisse Bamako, Banque courant…" autoFocus />
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-medium">
              Type
              <select aria-label="Type de compte" value={type} onChange={(e) => modifier(setType)(e.target.value)} className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm">
                {['CASH', 'BANK', 'MOBILE_MONEY'].map((code) => <option key={code} value={code}>{libelle('compteTresorerie', code)}</option>)}
              </select>
            </label>
            <label className="block text-sm font-medium">
              Solde d’ouverture
              <Input aria-label="Solde d’ouverture" type="number" value={solde} onChange={(e) => modifier(setSolde)(e.target.value)} className="mt-1" placeholder="Inconnu" />
            </label>
            <label className="block text-sm font-medium">
              Au
              <Input aria-label="Date du solde" type="date" value={date} disabled={solde === ''} onChange={(e) => modifier(setDate)(e.target.value)} className="mt-1" />
            </label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onFermer}>Annuler</Button>
          <Button type="button" disabled={envoi || !enLigne || !nom.trim()} onClick={enregistrer}>{envoi ? 'Création…' : 'Créer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ListeComptes = ({ comptes, total, devise, choisi, onChoisir, peutSaisir, onAjouter }) => (
  <aside className="hidden space-y-2 lg:block" aria-label="Comptes">
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-baseline justify-between gap-2 border-b bg-muted/40 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Comptes</span>
        <span className="text-sm font-semibold tabular-nums" title="Total des comptes au solde connu">{formatMontant(total, devise)}</span>
      </div>
      <ul>
        {comptes.map((compte) => (
          <li key={compte.id}>
            <button
              type="button"
              onClick={() => onChoisir(compte.id)}
              aria-current={choisi === compte.id ? 'true' : undefined}
              className={cn('flex w-full items-baseline justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/50', choisi === compte.id && 'bg-primary/10 font-medium')}
            >
              <span className="min-w-0">
                <span className="block truncate" title={compte.name}>{nomLisible(compte.name, 'Compte')}</span>
                <span className="block text-xs font-normal text-muted-foreground">{libelle('compteTresorerie', compte.account_type)}</span>
              </span>
              {compte.balance == null ? (
                <span className="shrink-0 rounded-full border border-flame/30 bg-flame/10 px-2 text-xs text-flame-ink">À compléter</span>
              ) : (
                <span className="shrink-0 tabular-nums">{formatMontant(compte.balance, compte.currency)}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
    {peutSaisir && (
      <Button type="button" variant="ghost" size="sm" className="h-8 w-full justify-start gap-1.5" onClick={onAjouter}>
        <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter un compte
      </Button>
    )}
  </aside>
);

export default function CaisseBanque({ position, positionStatut, compteChoisi, onChoisirCompte, periode, onToutesDates, peutSaisir = true, onModifie }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const recherche = searchParams.get('caisse-q') || '';
  const sens = searchParams.get('caisse-sens') || '';
  const termes = useDebouncedValue(recherche, 350);
  const changer = (cle, valeur) => setSearchParams((precedent) => {
    const suivant = new URLSearchParams(precedent);
    if (valeur) suivant.set(cle, valeur);
    else suivant.delete(cle);
    return suivant;
  }, { replace: cle === 'caisse-q' });

  const comptes = useMemo(() => position?.treasury?.accounts || [], [position]);
  // Sans compte choisi dans l'adresse, le premier compte (une caisse d'abord).
  const compteId = compteChoisi && comptes.some((c) => c.id === compteChoisi)
    ? compteChoisi
    : (comptes.find((c) => c.account_type === 'CASH') || comptes[0])?.id || '';
  const [livre, setLivre] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [ligneOuverte, setLigneOuverte] = useState(null);
  const [fenetre, setFenetre] = useState(null);
  const du = periode?.du || '';
  const au = periode?.au || '';
  const pagination = usePaginationServeur(livre?.total, {
    cle: 'caisse',
    reinitialiser: `${compteId}|${termes}|${sens}|${du}|${au}`,
    taillePrefere: 50,
  });
  const { page, taille } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(async () => {
    if (!compteId) return;
    const perimee = nouvelleDemande();
    setChargement(true);
    try {
      const resultat = await livreDeCaisse(compteId, { page, page_size: taille, q: termes.trim(), sens, du, au });
      if (perimee()) return;
      setLivre(resultat);
      setErreur('');
    } catch (e) {
      if (perimee()) return;
      setErreur(messageErreur(e, 'Le livre de caisse n’a pas pu être chargé.'));
    } finally {
      if (!perimee()) setChargement(false);
    }
  }, [compteId, page, taille, termes, sens, du, au, nouvelleDemande]);
  useEffect(() => {
    charger();
  }, [charger]);
  useRafraichirSur(['finance', 'paiements', 'factures', 'depenses'], charger);

  // Arrivée depuis « À faire » (« Compléter »): la fenêtre du solde s'ouvre seule.
  const demandeCompleter = searchParams.get('completer') === '1';
  useEffect(() => {
    if (!demandeCompleter || !livre || livre.account?.id !== compteId) return;
    if (peutSaisir) setFenetre('ouverture');
    changer('completer', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandeCompleter, livre, compteId, peutSaisir]);

  const apresGeste = () => {
    setFenetre(null);
    charger();
    onModifie?.();
  };

  const compte = livre?.account?.id === compteId ? livre.account : comptes.find((c) => c.id === compteId) || null;
  const devise = compte?.currency || position?.currency || 'XOF';
  const optionsComptes = useMemo(() => comptes.map((c) => ({
    value: c.id,
    label: nomLisible(c.name, 'Compte'),
    description: c.balance == null ? 'À compléter' : formatMontant(c.balance, c.currency),
  })), [comptes]);

  if (positionStatut === 'loading' && !position) {
    return <LoadingState shape="table" rows={6} columns={6} label="Chargement des comptes" />;
  }
  if (!comptes.length) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-3 py-2 text-sm">
        <span className="text-muted-foreground">{positionStatut === 'error' ? 'Les comptes sont indisponibles.' : 'Aucun compte de caisse ou de banque.'}</span>
        {peutSaisir && positionStatut !== 'error' && (
          <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setFenetre('compte')}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter un compte
          </Button>
        )}
        <NouveauCompte ouvert={fenetre === 'compte'} onFermer={() => setFenetre(null)} onTermine={apresGeste} />
      </div>
    );
  }

  const lignes = livre?.account?.id === compteId ? livre.items : [];
  const soldeInconnu = compte && compte.balance == null;
  const totaux = livre?.totals;
  const periodeLibelle = periode?.cle === 'tout' ? '' : periode?.libelle ? ` · ${periode.libelle}` : '';

  return (
    <div className="grid gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
      <ListeComptes
        comptes={comptes}
        total={position?.treasury?.total}
        devise={position?.currency}
        choisi={compteId}
        onChoisir={onChoisirCompte}
        peutSaisir={peutSaisir}
        onAjouter={() => setFenetre('compte')}
      />
      <section className="min-w-0 space-y-3" aria-label="Livre de caisse">
        <div className="lg:hidden">
          <SearchableSelect aria-label="Compte" value={compteId} onValueChange={onChoisirCompte} options={optionsComptes} placeholder="Choisir un compte" searchPlaceholder="Rechercher un compte…" emptyMessage="Aucun compte" />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-baseline gap-2">
            <h3 className="truncate text-base font-semibold">{nomLisible(compte?.name, 'Compte')}</h3>
            {soldeInconnu ? (
              <span className="rounded-full border border-flame/30 bg-flame/10 px-2 text-xs text-flame-ink">Solde à compléter</span>
            ) : (
              <strong className="tabular-nums" data-testid="solde-compte">{formatMontant(compte?.balance, devise)}</strong>
            )}
            {compte?.opening_balance != null && (
              <Aide titre="Solde d’ouverture">
                {`Solde d’ouverture ${formatMontant(compte.opening_balance, devise)}${compte.opening_date ? ` au ${formatDate(compte.opening_date)}` : ''}, puis les mouvements confirmés depuis cette date.`}
              </Aide>
            )}
          </div>
          {peutSaisir && (
            <div className="ml-auto flex flex-wrap gap-2">
              {soldeInconnu ? (
                <Button type="button" size="sm" className="h-8" onClick={() => setFenetre('ouverture')}>Compléter le solde</Button>
              ) : (
                <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setFenetre('ouverture')}>Solde d’ouverture</Button>
              )}
              <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => setFenetre('compter')} data-testid="bouton-compter-caisse">Compter la caisse</Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input type="search" aria-label="Rechercher dans le livre de caisse" placeholder="Rechercher…" value={recherche} onChange={(e) => changer('caisse-q', e.target.value)} className="h-8 pl-8 text-sm" />
          </div>
          <div className="flex gap-1 overflow-x-auto" role="group" aria-label="Filtrer par sens">
            {SENS_LIVRE.filter((s) => s.cle !== 'controles' || Number(compte?.checks_count) > 0).map((choix) => (
              <button
                key={choix.cle || 'tout'}
                type="button"
                aria-pressed={sens === choix.cle}
                onClick={() => changer('caisse-sens', choix.cle)}
                className={cn('inline-flex h-8 shrink-0 items-center rounded-full border px-3 text-xs font-medium transition-colors', sens === choix.cle ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}
              >
                {choix.titre}
              </button>
            ))}
          </div>
        </div>

        {erreur && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 p-3 text-sm text-destructive">
            <span>{erreur}</span>
            <Button variant="outline" size="sm" className="h-7" onClick={charger}>Réessayer</Button>
          </div>
        )}
        {!livre && !erreur && <LoadingState shape="table" rows={8} columns={6} label="Chargement du livre de caisse" />}
        {livre && (
          <div className={cn('overflow-x-auto rounded-xl border bg-card', chargement && 'opacity-70')}>
            <table className="w-full text-sm" data-testid="livre-de-caisse">
              <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold">Libellé</th>
                  <th className="hidden px-3 py-2 font-semibold md:table-cell">Référence</th>
                  <th className="hidden px-3 py-2 text-right font-semibold sm:table-cell">Entrée</th>
                  <th className="hidden px-3 py-2 text-right font-semibold sm:table-cell">Sortie</th>
                  <th className="px-3 py-2 text-right font-semibold"><span className="sm:hidden">Montant</span><span className="hidden sm:inline">Solde</span></th>
                </tr>
              </thead>
              <tbody>
                {lignes.length === 0 && (
                  <tr className="h-10">
                    <td colSpan={6} className="px-3 py-2 text-muted-foreground">
                      {recherche || sens ? 'Aucune ligne ne correspond.' : `Aucun mouvement${periodeLibelle ? ` en ${periode.libelle}` : ''}.`}
                      {!recherche && !sens && periode?.cle !== 'tout' && onToutesDates && (
                        <button type="button" className="ml-2 font-medium text-primary hover:underline" onClick={onToutesDates}>Voir toutes les dates</button>
                      )}
                    </td>
                  </tr>
                )}
                {lignes.map((ligne) => {
                  const { qui, nature } = libelleLigne(ligne);
                  const sansEffet = Boolean(ligne.etat);
                  const controle = ligne.kind === 'BALANCE_CHECK';
                  const signe = ligne.entree ? 1 : ligne.sortie ? -1 : 0;
                  const montant = ligne.entree || ligne.sortie;
                  return (
                    <tr
                      key={ligne.id}
                      className={cn('border-b last:border-0', !controle && 'cursor-pointer hover:bg-muted/40', sansEffet && 'text-muted-foreground')}
                      onClick={controle ? undefined : () => setLigneOuverte(ligne.id)}
                      tabIndex={controle ? undefined : 0}
                      onKeyDown={controle ? undefined : (e) => { if (e.key === 'Enter') setLigneOuverte(ligne.id); }}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                        <span className="sm:hidden">{formatDate(ligne.occurred_at).slice(0, 5)}</span>
                        <span className="hidden sm:inline">{formatDate(ligne.occurred_at)}</span>
                      </td>
                      <td className="w-full max-w-0 px-3 py-1.5">
                        <span className="block truncate" title={[qui, nature, ligne.reference].filter(Boolean).join(' · ')}>
                          <span className={cn(!sansEffet && 'text-foreground')}>{qui}</span>
                          {nature && <span className="text-muted-foreground"> · {nature}</span>}
                          {ligne.etat && (
                            <span className={cn('ml-2 rounded-full border px-1.5 text-[11px]', ligne.etat === 'attente' ? 'border-flame/30 bg-flame/10 text-flame-ink' : 'border-border bg-muted/50')}>{ETATS[ligne.etat]}</span>
                          )}
                        </span>
                        {controle && (
                          <span className="block text-xs">
                            {`Compté ${formatMontant(ligne.declared, devise)}`}
                            {ligne.difference != null && ` · écart ${Number(ligne.difference) > 0 ? '+ ' : Number(ligne.difference) < 0 ? '− ' : ''}${formatMontant(Math.abs(Number(ligne.difference)), devise)}`}
                          </span>
                        )}
                        <span className="block truncate text-xs text-muted-foreground md:hidden">{texteSur(ligne.reference, '')}</span>
                      </td>
                      <td className="hidden max-w-[12rem] truncate whitespace-nowrap px-3 py-1.5 text-muted-foreground md:table-cell" title={ligne.reference || undefined}>{texteSur(ligne.reference, '')}</td>
                      <td className={cn('hidden whitespace-nowrap px-3 py-1.5 text-right tabular-nums sm:table-cell', ligne.etat === 'annulee' && 'line-through')}>{montantOuVide(ligne.entree, devise)}</td>
                      <td className={cn('hidden whitespace-nowrap px-3 py-1.5 text-right tabular-nums sm:table-cell', ligne.etat === 'annulee' && 'line-through')}>{montantOuVide(ligne.sortie, devise)}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                        <span className="hidden sm:inline">{ligne.solde == null ? '' : formatMontant(ligne.solde, devise)}</span>
                        <span className="sm:hidden">
                          {montant ? <span className={cn('block', signe > 0 && !sansEffet && 'text-success')}>{signe > 0 ? '+ ' : '− '}{formatMontant(montant, devise)}</span> : null}
                          {ligne.solde != null && <span className="block text-xs text-muted-foreground">{formatMontant(ligne.solde, devise)}</span>}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totaux && Number(totaux.count) > 0 && (
                <tfoot className="border-t bg-muted/50 font-semibold" data-testid="pied-livre">
                  <tr>
                    <td className="px-3 py-2" colSpan={2}>
                      <span className="hidden sm:inline">Total{periodeLibelle} ({formatNombre(totaux.count)} ligne{Number(totaux.count) > 1 ? 's' : ''})</span>
                      <span className="sm:hidden">{`+ ${formatMontant(totaux.entrees, devise)} · − ${formatMontant(totaux.sorties, devise)}`}</span>
                    </td>
                    <td className="hidden md:table-cell" />
                    <td className="hidden whitespace-nowrap px-3 py-2 text-right tabular-nums sm:table-cell">{formatMontant(totaux.entrees, devise)}</td>
                    <td className="hidden whitespace-nowrap px-3 py-2 text-right tabular-nums sm:table-cell">{formatMontant(totaux.sorties, devise)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums" title="Solde à la fin de la période">
                      {livre.period?.closing == null ? 'À compléter' : formatMontant(livre.period.closing, devise)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        <BarrePagination pagination={pagination} nom="mouvements" />
      </section>

      <DetailEcriture operationId={ligneOuverte} onFermer={() => setLigneOuverte(null)} onModifie={() => { charger(); onModifie?.(); }} peutSaisir={peutSaisir} />
      <CompterCaisse compte={compte} ouvert={fenetre === 'compter'} onFermer={() => setFenetre(null)} onTermine={apresGeste} />
      <SoldeOuverture compte={compte} ouvert={fenetre === 'ouverture'} onFermer={() => setFenetre(null)} onTermine={apresGeste} />
      <NouveauCompte ouvert={fenetre === 'compte'} onFermer={() => setFenetre(null)} onTermine={apresGeste} />
    </div>
  );
}
